import React from "react";
import { AxiosError, AxiosPromise } from "axios";
import useSWR from "swr";
import { captureException, captureMessage } from "@sentry/nextjs";
import Cookies from "js-cookie";
import {
  APIResponse,
  LoadUserRejectObj,
  RejectObj,
  GetFeatureFlagResponse,
  SubmitShipListBody,
  SubmitShipListResponse,
  GetDesignerDirectoryResponse,
  UpdatePaymentOptions,
} from "../@types/api";
import { Brands } from "../@types/brands";
import { ProfileType } from "../@types/profile";
import { emptyUser } from "../components/context/contextHelper";
import { SellerAddress } from "../@types/address";
import { PendingItem } from "../@types/PendingItem";
import SYBForm from "../@types/syb-form";
import { COOKIES } from "../events/cookies";
import { CartType } from "../@types/cart";
import { AccountType } from "../@types/account";
import { transformAccountToProfile } from "../helpers/profile/transformAccountToProfile";
import { StripeFinancialConnectionAccount } from "../@types/stripeFinancialConnectionAccount";
import {
  accountURL,
  designerDirectoryUrl,
  featureFlagURL,
  shiplistApiURL,
  addressesURL,
  webappAPI,
  spAPI,
  paymentOptionsURL,
  stripeFinancialConnectionsClientSecretUrl,
  stripeFinancialConnectionsUpdateAccountUrl,
  stripeFinancialConnectionsDeleteAccountUrl,
  featureFlagsURL,
} from "./urls";
import axiosInstance, { axiosInstanceNoAuth } from "./axios-config";
import { getUserContext } from "../components/context/UserContext";

// bare axios (no instances, no headers) bc headers freakout API gateway for some reason
export const getDesignerDir = async (): Promise<Brands> => {
  try {
    const result = await axiosInstanceNoAuth.get<
      GetDesignerDirectoryResponse[]
    >(designerDirectoryUrl);

    const brands: Brands = result.data.reduce((brand, designer) => {
      brand[designer.id] = { name: designer.name };

      if (designer.brand_categories) {
        brand[designer.id].categories = designer.brand_categories.map(
          (category) => ({
            id: category.id,
            name: category.name,
          })
        );
      }

      return brand;
    }, {});

    return brands;
  } catch (error) {
    captureException(error);
    throw error;
  }
};

export const getFeatureFlag = async (key: string): Promise<boolean> => {
  try {
    const result = await axiosInstanceNoAuth.post<GetFeatureFlagResponse>(
      featureFlagURL,
      { key }
    );
    return result.data.isEnabled;
  } catch (error) {
    captureException("getFeatureFlag", error);
    return false;
  }
};

export const getFeatureFlags = async (
  keys: string[]
): Promise<Record<string, boolean>> => {
  try {
    const result = await axiosInstanceNoAuth.post<Record<string, boolean>>(
      featureFlagsURL,
      { keys }
    );
    console.log(result.data);
    return result.data;
  } catch (error) {
    captureException("getFeatureFlags", error);
    return Object.fromEntries(keys.map((key) => [key, false]));
  }
};

const collectMessages = (value: unknown): string[] => {
  if (value === null || value === undefined || value === "") {
    return [];
  }
  if (typeof value === "string") {
    return [value];
  }
  if (Array.isArray(value)) {
    return value.flatMap(collectMessages);
  }
  if (typeof value === "object") {
    return Object.values(value as Record<string, unknown>).flatMap(
      collectMessages
    );
  }
  return [];
};

// The ship-list endpoint reports validation failures under `errors` but shipping,
// label and pickup failures under `message`. Read both, or the carrier's actual
// rejection reason is dropped and the seller only sees "An error occurred."
const shipListErrorMessage = (payload: unknown): string => {
  const body = (payload && typeof payload === "object"
    ? payload
    : {}) as Record<string, unknown>;
  const messages = [
    ...collectMessages(body.errors),
    ...collectMessages(body.message),
  ];

  return messages.join(" ").trim() || "An error occurred.";
};

export const submitShipList = async (
  data: Partial<SubmitShipListBody>
): Promise<SubmitShipListResponse> => {
  let body: SubmitShipListResponse;

  try {
    const response = await axiosInstance.post<SubmitShipListResponse>(
      shiplistApiURL,
      data
    );
    body = response.data;
  } catch (error) {
    const payload = (error as AxiosError)?.response?.data;
    captureMessage(
      `submitShipList request failed: ${JSON.stringify(
        payload ?? (error as Error)?.message
      )}`
    );
    throw new Error(shipListErrorMessage(payload));
  }

  if ("success" in body && !body.success) {
    captureMessage(`submitShipList error: ${JSON.stringify(body)}`);
    throw new Error(shipListErrorMessage(body));
  }

  return body;
};

export const getShiplist = (): APIResponse<PendingItem[]> => {
  const { user } = getUserContext();
  const { market } = user;
  const { data, error, isValidating } = useSWR<
    { data: { data: PendingItem[] } },
    AxiosError
  >(
    typeof market !== "undefined"
      ? [shiplistApiURL, { params: { market } }]
      : null,
    axiosInstance.get,
    {
      errorRetryCount: 0,
      revalidateOnFocus: false,
    }
  );
  const pendingItems = data?.data?.data || [];
  return {
    data: pendingItems,
    isLoading: !error && !data && isValidating,
    error,
  };
};

const handleRejectObj = <K extends string>(
  err: AxiosError,
  rejectObj: RejectObj<K>,
  key: K,
  msgContext: string,
  type?: string
): void => {
  const errJson: { status?: number; message?: string } =
    err?.toJSON?.() || err || {};
  rejectObj.push({
    key,
    message: err?.message || errJson?.message || `Couldn't load ${msgContext}`,
    status: `${errJson?.status}` || "400",
    type,
  });
};

export const loadUser = async (): Promise<ProfileType> =>
  new Promise(
    // eslint-disable-next-line no-async-promise-executor
    async (res, rej): Promise<void> => {
      let user = emptyUser();
      const rejectObj: LoadUserRejectObj = [];
      try {
        const response = await axiosInstance.get(accountURL);
        if (!response?.data?.data) {
          rej();
          return;
        }

        const account: AccountType = response.data.data;
        const profile: ProfileType = transformAccountToProfile(account);

        const primaryAddressInclude = response.data.included?.find(
          (include) => include.type === "primaryAddress"
        );
        if (primaryAddressInclude) {
          user.address = primaryAddressInclude.attributes as SellerAddress;
        }

        user = { ...user, ...profile };
      } catch (error) {
        handleRejectObj(error, rejectObj, "user", "user data");
      }

      // only rej on a failed initial user load, not on payment or address
      if (rejectObj?.find((o) => o?.key === "user")) {
        rej(rejectObj);
        captureException("loadUser", {
          extra: { rejectObj: JSON.stringify(rejectObj) },
        });
      } else {
        res(user);
      }
    }
  );

export const loadCart = async (
  setCart: React.Dispatch<React.SetStateAction<CartType>>
): Promise<void> => {
  const identityId = Cookies.get(COOKIES.UUID);

  try {
    const { data } = await axiosInstance.get(
      `${process.env.NEXT_PUBLIC_NODE_APP_URL}/cart`,
      {
        headers: {
          "x-fp-identityid": identityId,
        },
      }
    );

    const inStockItems =
      data?.data?.cartItems?.filter((cartItem) => cartItem.isSold === false) ??
      [];

    setCart({
      loading: false,
      isLoggedIn: data.isSignedIn,
      cartAmount: inStockItems.length,
      isDNS: data?.data?.user?.dns ?? false,
    });
  } catch (err) {
    captureException("loadCart", err);
  }
};

/* NB 'phone' must be a pure numeric-string */
export const updateAddress = async (
  params: SellerAddress
): Promise<AxiosPromise> => {
  const json = await axiosInstance.post<SellerAddress>(addressesURL, params);
  return json;
};

export const updatePaymentOptions = async (
  data: UpdatePaymentOptions
): Promise<boolean> => {
  try {
    const result = await axiosInstance.patch<AccountType>(
      paymentOptionsURL,
      data
    );
    return true;
  } catch (error) {
    captureException("updatePaymentOptions", error);
    throw error;
  }
};

export const saveDraft = async (
  draftId: number,
  userEmail: string,
  values: SYBForm
): Promise<{ data: { id: number } }> => {
  const { currentBrand, currentCategory, itemName, description } = values;

  const data: Record<string, string | number> = {
    email: userEmail,
    name: itemName,
    brandId: currentBrand,
  };

  if (
    currentCategory &&
    !/select/i.test(currentCategory) &&
    currentCategory !== "undefined"
  ) {
    data.categoryId = currentCategory;
  }
  if (description && description.length > 0) {
    data.description = description;
  }
  if (draftId) {
    data.id = draftId;
  }

  return axiosInstanceNoAuth.put(`${spAPI}/quotes/drafts`, data);
};

export const getProductSuggestions = (
  search: string
): Promise<{ data: string[]; status: number; statusText: string }> =>
  axiosInstance.get(`${webappAPI}/products/suggest?search=${search}`);

export const getStripeFinancialConnectionsClientSecret = async (): Promise<
  string
> => {
  const response = await axiosInstance.get(
    stripeFinancialConnectionsClientSecretUrl
  );
  return response.data.client_secret;
};

export const updateStripeFinancialConnectionsAccount = async (
  stripeAccount: StripeFinancialConnectionAccount
): Promise<void> => {
  await axiosInstance.post(
    stripeFinancialConnectionsUpdateAccountUrl,
    stripeAccount
  );
};

export const deleteStripeFinancialConnectionsAccount = async (): Promise<
  void
> => {
  await axiosInstance.delete(stripeFinancialConnectionsDeleteAccountUrl);
};
